import React, { useState, useRef } from 'react';
import { Upload, FileText, Loader2, Sparkles, ClipboardType, CheckCircle2, AlertTriangle, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import api from '@/lib/api';
import { SOURCE_LABEL, UPLOAD_SOURCE_OPTIONS } from '@/lib/format';
import { toast } from 'sonner';
import { track } from '@/lib/analytics';

const ACCEPT = '.pdf,.docx,.eml,.msg,.txt';

export const EventUploadDialog = ({ projectId, onDone, trigger }) => {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('paste');
  const [sourceType, setSourceType] = useState('email');
  const [subject, setSubject] = useState('');
  const [sender, setSender] = useState('');
  const [content, setContent] = useState('');
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef();

  const reset = () => { setSubject(''); setSender(''); setContent(''); setFile(null); setResult(null); };

  const finish = (analysis) => {
    setResult(analysis);
    track('event_uploaded', { via: 'upload', source_type: sourceType, status: analysis?.status });
    if (analysis?.status === 'scope_change') toast.success(`Scope change detected: ${analysis.title || ''}`);
    else if (analysis?.status === 'no_change') toast('Analyzed — within original scope.');
    else if (analysis?.status === 'failed') toast.error('AI analysis failed. The event was still saved.');
    else if (analysis?.status === 'skipped') toast('Saved. No extractable text to analyze.');
    onDone && onDone();
  };

  const submitPaste = async () => {
    if (!content.trim()) { toast.error('Paste the event text first'); return; }
    setLoading(true); setResult(null);
    try {
      const r = await api.post('/detections/analyze', { project_id: projectId, source_type: sourceType, subject, sender, content });
      finish(r.data.analysis);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Analysis failed'); }
    finally { setLoading(false); }
  };

  const submitFile = async () => {
    if (!file) { toast.error('Choose a file'); return; }
    setLoading(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('project_id', projectId);
      fd.append('source_type', sourceType);
      if (subject) fd.append('subject', subject);
      if (sender) fd.append('sender', sender);
      const r = await api.post('/events/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (r.data.extraction?.status !== 'ok') {
        toast.error(r.data.extraction?.error || 'Could not extract text');
        setResult({ status: 'skipped', extraction: r.data.extraction });
      } else {
        finish({ ...r.data.analysis, extraction: r.data.extraction });
      }
    } catch (e) { toast.error(e?.response?.data?.detail || 'Upload failed'); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        {trigger || <Button className="bg-money hover:bg-money-700" data-testid="upload-event-btn"><Upload className="h-4 w-4 mr-2" />Upload Event</Button>}
      </DialogTrigger>
      <DialogContent className="max-w-lg" data-testid="event-upload-dialog">
        <DialogHeader><DialogTitle>Analyze a commercial event</DialogTitle></DialogHeader>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Document type</Label>
            <Select value={sourceType} onValueChange={setSourceType}>
              <SelectTrigger className="mt-1" data-testid="event-source-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {UPLOAD_SOURCE_OPTIONS.map((k) => <SelectItem key={k} value={k}>{SOURCE_LABEL[k] || k}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">From / sender (optional)</Label>
            <Input className="mt-1" value={sender} onChange={(e) => setSender(e.target.value)} placeholder="e.g. Owner rep" />
          </div>
        </div>
        <div>
          <Label className="text-xs">Subject (optional)</Label>
          <Input className="mt-1" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="e.g. RFI 88 - extra ductwork" data-testid="event-subject" />
        </div>

        <Tabs value={tab} onValueChange={setTab} className="mt-1">
          <TabsList className="w-full">
            <TabsTrigger value="paste" className="flex-1" data-testid="tab-paste"><ClipboardType className="h-4 w-4 mr-1.5" />Paste text</TabsTrigger>
            <TabsTrigger value="file" className="flex-1" data-testid="tab-file"><FileText className="h-4 w-4 mr-1.5" />Upload file</TabsTrigger>
          </TabsList>
          <TabsContent value="paste" className="mt-3">
            <Textarea rows={6} value={content} onChange={(e) => setContent(e.target.value)} placeholder="Paste the email, RFI, site instruction or meeting note..." data-testid="event-content" />
            <Button onClick={submitPaste} disabled={loading} className="w-full mt-3 bg-money hover:bg-money-700" data-testid="event-analyze-paste">
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />} Analyze &amp; Detect
            </Button>
          </TabsContent>
          <TabsContent value="file" className="mt-3">
            <input ref={fileRef} type="file" accept={ACCEPT} className="hidden" data-testid="event-file-input"
              onChange={(e) => setFile(e.target.files?.[0] || null)} />
            <button type="button" onClick={() => fileRef.current?.click()}
              className="w-full rounded-xl border-2 border-dashed border-slate-200 hover:border-brand/40 bg-slate-50 px-4 py-6 text-center transition-colors">
              <Upload className="h-5 w-5 mx-auto text-slate-400" />
              <div className="text-sm text-slate-600 mt-2">{file ? file.name : 'Click to choose a file'}</div>
              <div className="text-[11px] text-slate-400 mt-1">PDF, DOCX, EML, MSG, TXT — no OCR (scanned PDFs: paste text)</div>
            </button>
            <Button onClick={submitFile} disabled={loading || !file} className="w-full mt-3 bg-money hover:bg-money-700" data-testid="event-analyze-file">
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />} Extract, Analyze &amp; Detect
            </Button>
          </TabsContent>
        </Tabs>

        {result && (
          <div className="mt-2 rounded-lg border p-3 text-sm" data-testid="event-upload-result">
            {result.status === 'scope_change' && (
              <div className="flex items-start gap-2 text-warn-700"><AlertTriangle className="h-4 w-4 mt-0.5" />
                <div><span className="font-semibold">Scope change detected.</span> {result.title}</div></div>
            )}
            {result.status === 'no_change' && (
              <div className="flex items-start gap-2 text-money-700"><CheckCircle2 className="h-4 w-4 mt-0.5" />
                <div><span className="font-semibold">Within original scope.</span> {result.summary}</div></div>
            )}
            {(result.status === 'skipped' || result.status === 'failed') && (
              <div className="flex items-start gap-2 text-slate-600"><Info className="h-4 w-4 mt-0.5" />
                <div>{result.extraction?.error || 'Saved. Not analyzed.'}</div></div>
            )}
            {result.truncated_for_ai && (
              <div className="mt-2 text-[11px] text-amber-700">Note: the document was long and was truncated for AI analysis.</div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
